<script setup>
import Button from 'dashboard/components-next/button/Button.vue';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { actionTypeKey, escalationState } from 'dashboard/helper/squareme';

const props = defineProps({
  escalations: { type: Array, default: () => [] },
  // Private notes on the conversation, keyed by escalation reference.
  notesByReference: { type: Object, default: () => ({}) },
  retrying: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['retry']);

// A failed send already raised again (same action and payload, newer) offers no retry.
const canRetry = (escalation, index) =>
  escalationState(escalation) === 'send_failed' &&
  !props.escalations
    .slice(0, index)
    .some(
      newer =>
        newer.action_type === escalation.action_type &&
        JSON.stringify(newer.payload) === JSON.stringify(escalation.payload)
    );

const STATE_CLASSES = {
  sending: 'bg-n-slate-3 text-n-slate-11',
  awaiting: 'bg-n-amber-3 text-n-amber-11',
  approved: 'bg-n-teal-3 text-n-teal-11',
  rejected: 'bg-n-ruby-3 text-n-ruby-11',
  expired: 'bg-n-slate-3 text-n-slate-11',
  send_failed: 'bg-n-ruby-3 text-n-ruby-11',
  refused: 'bg-n-ruby-3 text-n-ruby-11',
  not_done: 'bg-n-ruby-3 text-n-ruby-11',
};
</script>

<template>
  <ul class="mb-0 flex list-none flex-col gap-2">
    <li
      v-for="(escalation, index) in escalations"
      :key="escalation.id"
      data-test-id="squareme-escalation"
      class="flex flex-col gap-1 rounded-lg border border-n-weak px-3 py-2"
    >
      <div class="flex flex-wrap items-center justify-between gap-1">
        <span class="font-medium text-n-slate-12">
          {{
            $t(
              `SQUAREME_PANEL.ACTION_TYPES.${actionTypeKey(escalation.action_type)}`
            )
          }}
        </span>
        <span
          data-test-id="squareme-escalation-state"
          class="whitespace-nowrap rounded px-1.5 text-xs"
          :class="STATE_CLASSES[escalationState(escalation)]"
        >
          {{
            $t(
              `SQUAREME_PANEL.ESCALATIONS.STATE.${escalationState(escalation)}`
            )
          }}
        </span>
      </div>
      <p class="mb-0 text-xs text-n-slate-11">
        {{
          $t(
            `SQUAREME_PANEL.ESCALATIONS.STATE_HINT.${escalationState(escalation)}`
          )
        }}
      </p>
      <p
        v-if="escalation.last_error"
        data-test-id="squareme-escalation-why"
        class="mb-0 text-xs text-n-ruby-11"
      >
        {{ escalation.last_error }}
      </p>
      <p class="mb-0 text-xs text-n-slate-12">
        <span class="text-n-slate-11">
          {{ $t('SQUAREME_PANEL.ESCALATIONS.REASON') }}:
        </span>
        {{ escalation.reason }}
      </p>
      <p class="mb-0 text-xs text-n-slate-11">
        {{
          $t('SQUAREME_PANEL.ESCALATIONS.RAISED', {
            time: dynamicTime(escalation.created_at),
          })
        }}
        · {{ escalation.reference }}
      </p>
      <div
        v-if="(notesByReference[escalation.reference] || []).length"
        data-test-id="squareme-escalation-notes"
        class="flex flex-col gap-1 rounded bg-n-amber-2 px-2 py-1 text-xs"
      >
        <span class="font-medium text-n-slate-12">
          {{ $t('SQUAREME_PANEL.ESCALATIONS.NOTES') }}
        </span>
        <p
          v-for="note in notesByReference[escalation.reference]"
          :key="note.id"
          class="mb-0 text-xs text-n-slate-12"
        >
          {{ note.content }}
        </p>
      </div>
      <Button
        v-if="canRetry(escalation, index)"
        size="xs"
        variant="faded"
        color="ruby"
        icon="i-lucide-rotate-cw"
        data-test-id="squareme-escalation-retry"
        :is-loading="!!retrying[escalation.id]"
        :label="$t('SQUAREME_PANEL.ESCALATIONS.RETRY')"
        @click="emit('retry', escalation)"
      />
    </li>
  </ul>
</template>
